import React, { useCallback } from 'react';

import { Typography } from '@mui/material';
import { GridLogicOperator } from '@mui/x-data-grid-premium';
import type {
  GridApi,
  GridColDef,
  GridFilterItem,
  GridFilterModel,
  GridTranslationKeys,
} from '@mui/x-data-grid-premium';
import clsx from 'clsx';
import { SelectButton } from 'src/components';
import { ReportAddFilterIcon } from 'src/components/icons/ReportAddFilterIcon';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuid4 } from 'uuid';

import { ReportFiltersPanelRow } from './ReportFiltersPanelRow';

/**
 * Layout copied from the grid's own panel slots rather than rendered with
 * them: `GridPanelContent` and `GridPanelFooter` read `useGridRootProps`, so
 * they throw wherever this core renders outside a DataGrid (the AI report
 * approval card).
 */
const useStyles = makeStyles()(theme => ({
  buttonIcon: { marginRight: theme.spacing(1) },
  content: {
    display: 'flex',
    flex: '1 1',
    flexDirection: 'column',
    gap: theme.spacing(1),
    maxHeight: 400,
    overflow: 'auto',
    padding: theme.spacing(2),
  },
  contentCompact: { gap: theme.spacing(0.5), padding: theme.spacing(1) },
  contentDisabled: { opacity: 0.6, pointerEvents: 'none' },
  footer: {
    alignItems: 'center',
    borderTop: `1px solid ${theme.appColors.rule}`,
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
    padding: theme.spacing(1),
  },
}));

export interface ReportFilterPanelLocaleText {
  readonly addFilter: string;
  readonly and: string;
  readonly deleteFilter: string;
  readonly getOperatorLabel: (key: GridTranslationKeys) => unknown;
  readonly or: string;
}

export interface ReportFiltersPanelCoreProps {
  readonly columns: readonly GridColDef[];
  readonly filterModel: GridFilterModel;
  readonly onFilterModelChange: (filterModel: GridFilterModel) => void;
  readonly addFilterColumns?: readonly GridColDef[];
  readonly apiRef?: React.RefObject<GridApi | null>;
  readonly compact?: boolean;
  readonly disableAddFilterButton?: boolean;
  readonly disabled?: boolean;
  readonly localeText?: ReportFilterPanelLocaleText;
}

const DEFAULT_LOCALE_TEXT: ReportFilterPanelLocaleText = {
  addFilter: 'Add filter',
  and: 'And',
  deleteFilter: 'Delete filter',
  getOperatorLabel: () => undefined,
  or: 'Or',
};

export const ReportFiltersPanelCore = React.memo(function ReportFiltersPanelCore({
  addFilterColumns,
  apiRef,
  columns,
  compact = false,
  disableAddFilterButton = false,
  disabled = false,
  filterModel,
  localeText = DEFAULT_LOCALE_TEXT,
  onFilterModelChange,
}: ReportFiltersPanelCoreProps) {
  const { classes } = useStyles();
  const items = filterModel.items;
  const hasMultipleFilters = items.length > 1;
  const logicOperator = filterModel.logicOperator ?? GridLogicOperator.And;
  const filterableColumns = columns.filter(column => column.filterable !== false);
  const filterableAddColumns = (addFilterColumns ?? columns).filter(
    column => column.filterable !== false
  );

  const createFilter = useCallback(() => {
    const firstFilterableColumn = filterableAddColumns.at(0);
    if (!firstFilterableColumn) return;

    const newItem: GridFilterItem = {
      field: firstFilterableColumn.field,
      id: uuid4(),
      operator: firstFilterableColumn.filterOperators?.at(0)?.value || 'contains',
      value: undefined,
    };
    onFilterModelChange({ ...filterModel, items: [...items, newItem] });
  }, [filterModel, filterableAddColumns, items, onFilterModelChange]);

  const applyFilterLogicOperator = useCallback(
    (operator: GridLogicOperator) => {
      onFilterModelChange({ ...filterModel, logicOperator: operator });
    },
    [filterModel, onFilterModelChange]
  );

  return (
    <>
      {Boolean(items.length) && (
        <div
          className={clsx(
            classes.content,
            compact && classes.contentCompact,
            disabled && classes.contentDisabled
          )}
        >
          {items.map((item, index) => (
            <ReportFiltersPanelRow
              // Remount uncontrolled inputs whenever their field or operator changes.
              key={`${item.id ?? index}:${item.field}:${item.operator}`}
              apiRef={apiRef}
              applyFilterChanges={nextItem => {
                onFilterModelChange({
                  ...filterModel,
                  items: items.map((currentItem, itemIndex) =>
                    itemIndex === index ? nextItem : currentItem
                  ),
                });
              }}
              applyMultiFilterOperatorChanges={applyFilterLogicOperator}
              columns={filterableColumns}
              compact={compact}
              deleteFilter={() => {
                onFilterModelChange({
                  ...filterModel,
                  items: items.filter((_currentItem, itemIndex) => itemIndex !== index),
                });
              }}
              disableMultiFilterOperator={index !== 1}
              hasMultipleFilters={hasMultipleFilters}
              item={item}
              localeText={localeText}
              multiFilterOperator={logicOperator}
              showMultiFilterOperators={index > 0}
            />
          ))}
        </div>
      )}
      {!disableAddFilterButton && !disabled && (
        <div className={classes.footer}>
          <SelectButton onClick={createFilter}>
            <ReportAddFilterIcon className={classes.buttonIcon} />
            <Typography color="primary" variant="body1">
              {localeText.addFilter}
            </Typography>
          </SelectButton>
        </div>
      )}
    </>
  );
});
